import * as React from 'react';
import styles from './Filters.scss';
import { shallowEqual } from 'react-redux';
import { arrayToObjectByKey, formatNumber, getDiapasonOfNumbers, getLevelFromRomanFormat, getNumbersFromDiapason, isEmptyObject } from '~/utils/utils';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import { filtersInfo, getFiltersInfo, HIDDEN_FILTERS_NAMES } from '~/settings/filtersMap';
import { t } from '~/utils/localization';
import Tag from '~/components/Tag/Tag';
import { FadeInDiv } from '~/components/Transitions';
import { Interpolate } from '@wg/wows-react-uikit';
import { getCurrency } from '~/utils/currencies';
import classNames from 'classnames';
import { FILTER_SHIP_TIER_NAME, FILTER_SHIP_CLASS_NAME, FILTER_NATION_NAME, FILTER_CURRENCY_NAME, FILTER_RARITY } from '~/const';
import { RootState, useAppSelector } from '~/Store';
import { ManagerData } from '~/core/ManagerData';
import { WarmingUpCollections } from '@wg/wows-entities/const';

export type ActiveFiltersValues = {
    values: any[];
    diapasons?: Record<string, IRange>;
};
export type ActiveFiltersType = Partial<Record<FILTER_INFO_NAME, ActiveFiltersValues>>;

interface IActiveFilter {
    activeFilters: ActiveFiltersType;
    resetAll: (event: React.MouseEvent) => void;
    onClick: (filterName: FILTER_INFO_NAME, value: any) => void;
    className?: string;
}

interface IRenderActiveFilterByType {
    type: FILTER_INFO_NAME;
    filters: ActiveFiltersValues;
    onClick?: (filterName: FILTER_INFO_NAME, value: any) => void;
}

const stateSelector = (state: RootState) => {
    return {
        nations: state.app.nations,
        vehicleTypes: state.app.vehicleTypes,
    };
};

export const RenderActiveFilterByType = ({ type, filters, onClick }: IRenderActiveFilterByType) => {
    const appState = useAppSelector(stateSelector, shallowEqual);
    const data = arrayToObjectByKey(getFiltersInfo()[type].items, 'value');
    const nations = appState.nations || {};
    const vehicleTypes = appState.vehicleTypes || {};
    let diapasonLevels: string[] | null = null;

    if (type === FILTER_SHIP_TIER_NAME) {
        const numbers = filters.values.map((item) => parseInt(item, 10));
        diapasonLevels = getDiapasonOfNumbers(numbers);

        return (
            <React.Fragment>
                {diapasonLevels.map((diapason) => {
                    let content = null;
                    const diapasonData = diapason.split ? diapason.split('-') : [];
                    let numbers: number[] | number;
                    if (diapasonData.length > 1) {
                        content = `${getLevelFromRomanFormat(+diapasonData[0])}-${getLevelFromRomanFormat(+diapasonData.pop())}`;
                        numbers = getNumbersFromDiapason(diapason);
                    } else {
                        content = getLevelFromRomanFormat(+diapason);
                        numbers = +diapason;
                    }

                    return (
                        <FadeInDiv key={`${type}_${diapason}`}>
                            <Tag
                                classNames={{ tag: styles.filterTag }}
                                label={content}
                                showCloseIcon={true}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClick(type, numbers);
                                }}
                            />
                        </FadeInDiv>
                    );
                })}
            </React.Fragment>
        );
    }

    if (type === FILTER_NATION_NAME || type === FILTER_SHIP_CLASS_NAME) {
        return (
            <React.Fragment>
                {filters.values.map((filterName: string, index) => {
                    const data = type === FILTER_NATION_NAME ? nations[filterName] : vehicleTypes[filterName];
                    const iconClassNames = classNames(
                        {
                            [styles.iconClass]: type === FILTER_SHIP_CLASS_NAME,
                            [styles.activeFilterItemClassIcon]: type === FILTER_SHIP_CLASS_NAME,
                            [styles.iconNation]: type === FILTER_NATION_NAME,
                        },
                        type === FILTER_NATION_NAME && styles[filterName],
                    );

                    const icon = type === FILTER_NATION_NAME ? (data as INation).icons.tiny || data.icons.default : data.icons.default;

                    return (
                        <FadeInDiv key={filterName + type + index}>
                            <Tag
                                classNames={{ tag: styles.filterTag }}
                                tooltipBody={<DefaultTooltip text={data.title} />}
                                showCloseIcon={true}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClick?.(type, filterName);
                                }}
                                label={
                                    <div
                                        className={iconClassNames}
                                        style={{
                                            backgroundImage: `url(${icon})`,
                                        }}
                                    />
                                }
                            />
                        </FadeInDiv>
                    );
                })}
            </React.Fragment>
        );
    }

    if (type === FILTER_RARITY) {
        const cache = ManagerData.getDataBySection(WarmingUpCollections.rarity);

        return (
            <React.Fragment>
                {filters.values.map((filterName: string, index) => {
                    const currentFilter = filtersInfo[FILTER_RARITY].items.find((item) => item.value === filterName);
                    return (
                        <FadeInDiv key={filterName + type + index}>
                            <Tag
                                classNames={{ tag: styles.filterTag }}
                                tooltipBody={<DefaultTooltip text={currentFilter.title} />}
                                showCloseIcon={true}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClick?.(type, filterName);
                                }}
                                label={
                                    <div
                                        className={styles.activeRatityIcon}
                                        style={{
                                            backgroundImage: `url(${cache[filterName].filterIcon})`,
                                        }}
                                    />
                                }
                            />
                        </FadeInDiv>
                    );
                })}
            </React.Fragment>
        );
    }

    if (type === FILTER_CURRENCY_NAME) {
        return (
            <React.Fragment>
                {filters.values.map((filterName: string) => {
                    const currencyName = data[filterName].value;
                    const diapason = filters.diapasons?.[filterName];
                    let min, max;

                    if (diapason?.min && diapason?.max) {
                        [min, max] = [diapason.min, diapason.max];
                    }

                    const currencyConfig = getCurrency(currencyName as string);
                    const icon = currencyConfig?.icons?.default;

                    const currencyText = t('{currencyName} {min} - {max}').replace('{currencyName}', '%(currencyName)s').replace('{min}', '%(min)s').replace('{max}', '%(max)s');

                    return (
                        <FadeInDiv key={filterName}>
                            <Tag
                                label={
                                    <Interpolate
                                        str={min === max || min === undefined || max === undefined ? '%(currencyName)s' : currencyText}
                                        min={formatNumber(min)}
                                        max={formatNumber(max)}
                                        currencyName={
                                            <div
                                                className={styles.activeCurrencyIcon}
                                                style={{
                                                    backgroundImage: `url(${icon})`,
                                                }}
                                            />
                                        }
                                    />
                                }
                                tooltipBody={<DefaultTooltip text={currencyConfig.title} />}
                                classNames={{ tag: styles.filterCurrencyTag, closeIcon: styles.currencyIconCloseTag }}
                                showCloseIcon={true}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClick?.(type, filterName);
                                }}
                            />
                        </FadeInDiv>
                    );
                })}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {filters.values.map((filterName: string) => {
                return (
                    <FadeInDiv key={filterName}>
                        <Tag
                            label={data[filterName].title}
                            classNames={{ tag: styles.filterTag }}
                            showCloseIcon={true}
                            onClick={(event) => {
                                event.stopPropagation();
                                onClick?.(type, filterName);
                            }}
                        />
                    </FadeInDiv>
                );
            })}
        </React.Fragment>
    );
};

const ActiveFilters = ({ className, activeFilters, resetAll, onClick }: IActiveFilter) => {
    return (
        <div className={classNames(styles.activeFiltersWrapper, styles.activeFilterContent, className)}>
            {Object.keys(activeFilters).map((key: FILTER_INFO_NAME, index) => {
                if (HIDDEN_FILTERS_NAMES.includes(key) || !activeFilters?.[key]?.values.length) {
                    return;
                }

                return <RenderActiveFilterByType key={`${key}_${index}`} type={key} filters={activeFilters?.[key]} onClick={onClick} />;
            })}
            {!isEmptyObject(activeFilters) && (
                <FadeInDiv>
                    <Tag
                        label={<div className={styles.resetFilters}>{t('Сбросить все')}</div>}
                        showCloseIcon
                        classNames={{
                            tag: styles.filterResetTag,
                        }}
                        onClick={resetAll}
                    />
                </FadeInDiv>
            )}
        </div>
    );
};

export default ActiveFilters;
